import { ObjPathProxy, createProxy, getPath } from "ts-object-path";
import i18next, { ResourceLanguage } from "i18next";

import { useTranslation } from "react-i18next";

export interface ResourceDictionary extends ResourceLanguage {
  Routes: {
    Login: string;
    Employees: string;
    EmployeeDetail: string;
    EmployeeEdit: string;
    EmployeeCreate: string;
    Guidepost: string;
    Dashboard: string;
    Users: string;
    UserDetail: string;
    Companies: string;
    CompanyDetail: string;
    CompanyEdit: string;
    CompanyCreate: string;
    AccessRoles: string;
    AccessRoleDetail: string;
    InvoiceItemCategories: string;
    InvoiceItemCategoryDetail: string;
    InvoiceItemCategoryCreate: string;
    Error403: string;
    Error404: string;
  };
  Errors: {
    APIError: {
      503: string;
      500: string;
    };
    ApplicationError: {
      Title: string;
      Subtitle: string;
    };
    Error404: {
      Title: string;
      Subtitle: string;
    };
    Error403: {
      Title: string;
      Subtitle: string;
    };
  };
  Login: {
    FormTitle: string;
    FormSubTitle: string;
    Login: {
      Label: string;
    };
    Password: {
      Label: string;
    };
    Submit: string;
    Error: {
      General: string;
      InvalidLogin: string;
    };
  };
  Auth: {
    Google: {
      Button: string;
      Redirect: {
        Title: string;
        SubTitle: string;
      };
    };
    Logout: string;
  };
  Dashboard: {
    Title: string;
    Breadcrumb: {
      Title: string;
    };
    Logout: string;
    Tickets: {
      Reminders: string;
      Warnings: string;
      Confirm: string;
      Resolved: string;
      Cancel: string;
      FixUntil: {
        Label: string;
        Today: string;
        Yesterday: string;
        Tomorrow: string;
        BeforeDays: string;
        AfterFewDays: string;
        AfterManyDays: string;
      };
      Subordinates: {
        Title: string;
      };
      Modal: {
        Title: string;
        Text: string;
        InputLabel: string;
        Error: string;
        Close: string;
        Confirm: string;
      };
    };
    RecentActivities: {
      Title: string;
      NoActivities: {
        Title: string;
        Text: string;
      };
    };
  };
  Validation: {
    Required: string;
    InvalidEmail: string;
  };
  MenuItems: {
    Dashboard: string;
    Guidepost: string;
  };
  Users: {
    Title: string;
    Breadcrumb: {
      Title: string;
    };
    CheckboxIsActive: string;
    Status: {
      Active: string;
      InActive: string;
      None: string;
    };
    List: {
      SearchBar: string;
      Name: string;
      Email: string;
      LastLoginDate: string;
      Status: string;
      Delete: {
        Success: string;
        Fail: string;
      };
    };
    Detail: {
      Title: string;
      Breadcrumb: {
        Title: string;
      };
      LastLoginDate: {
        Title: string;
      };
      Status: {
        Title: string;
        IsActive: string;
        InActive: string;
      };
      AccessRoles: {
        Title: string;
      };
      Save: string;
      SaveSuccess: string;
      SaveFail: string;
      BasicInfo: {
        Title: string;
        FirstName: string;
        LastName: string;
        Login: string;
        Status: string;
      };
    };
  };
  Companies: {
    Title: string;
    Customer: string;
    Supplier: string;
    SelectBox: {
      None: string;
      Customer: string;
      Supplier: string;
      CustomerAndSupplier: string;
    };
    List: {
      BtnCreate: {
        Title: string;
      };
      SearchBar: string;
      Name: string;
      Street: string;
      PostalCode: string;
      City: string;
      Role: string;
      LastUpdate: string;
      Detail: {
        Tooltip: string;
      };
      Edit: {
        Tooltip: string;
      };
      Delete: {
        Tooltip: string;
        ConfirmTitle: string;
        ConfirmText: string;
        Success: string;
        Fail: string;
      };
      Close: {
        Tooltip: string;
      };
      Breadcrumb: {
        Title: string;
      };
    };
    Detail: {
      Title: string;
      Address: string;
      IdentificationNumber: string;
      TaxIdentificationNumber: string;
      Role: string;
      Signature: string;
      Documents: {
        Title: string;
      };
      Breadcrumb: {
        Title: string;
      };
    };
  };
  CompanyEdit: {
    Title: string;
    NewCompany: string;
    Breadcrumb: {
      Title: string;
    };
    Save: string;
    Name: {
      Label: string;
      Validation: string;
    };
    IdentificationNumber: {
      Label: string;
      Validation: {
        Required: string;
        Regex: string;
        Max: string;
        Equals: string;
      };
    };
    TaxIdentificationNumber: {
      Label: string;
      Validation: {
        Max: string;
      };
    };
    Street: {
      Label: string;
      Validation: string;
    };
    StreetNo: {
      Label: string;
      Validation: string;
    };
    PostalCode: {
      Label: string;
      Validation: string;
    };
    City: {
      Label: string;
      Validation: string;
    };
    IsCustomer: {
      Label: string;
    };
    IsSupplier: {
      Label: string;
    };
    Role: {
      Label: string;
    };
    Image: {
      Label: string;
      Validation: {
        FileExtension: string;
        MaxSize: string;
      };
      Input: {
        Label: string;
        Selected: string;
      };
      Btn: {
        Select: string;
        Delete: string;
      };
    };
    Documents: {
      Label: string;
    };
    SaveSuccess: string;
    SaveFail: string;
    ContactEdit: {
      NewContact: string;
      FirstName: {
        Label: string;
        Validation: string;
      };
      LastName: {
        Label: string;
        Validation: string;
      };
      Email: {
        Label: string;
        Validation: string;
        WrongFormat: string;
      };
      Phone: {
        Label: string;
        Validation: string;
        WrongFormat: string;
      };
      ManDayCharge: {
        Label: string;
        Tooltip: string;
      };

      Position: {
        Label: string;
        Validation: string;
      };
      Save: {
        Success: string;
        Error: string;
      };
    };
  };
  AccessRoles: {
    Title: string;
    Breadcrumb: {
      Title: string;
    };
    List: {
      SearchBar: string;
      Name: string;
      Code: string;
      Delete: {
        Tooltip: string;
        ConfirmTitle: string;
        ConfirmText: string;
      };
    };
    Detail: {
      Title: string;
      Breadcrumb: {
        Title: string;
      };
      AccessRights: string;
      SearchBar: string;
      Save: string;
      SaveSuccess: string;
      SaveFail: string;
    };
  };
  Guidepost: {
    Title: string;
    Close: string;
    Description: {
      Title: string;
      Subtitle: string;
    };
    Recent: string;
    External: string;
  };
  HumanResources: {
    EmployeeList: {
      Title: string;
      Breadcrumb: {
        Title: string;
      };
    };
    Employees: {
      BtnCreate: {
        Title: string;
      };
      Breadcrumb: {
        Title: string;
      };
      CollapsingButton: string;
      ExpandButton: string;
      Subsidiaries: {
        One: string;
        More: string;
        Many: string;
      };
      Row: {
        Name: string;
        Departments: string;
        Position: string;
        DateUpdated: string;
        Anniversary: string;
        Detail: string;
      };
      Detail: {
        Title: string;
        Breadcrumb: {
          Title: string;
        };
        Edit: string;
        Delete: {
          Label: string;
          ConfirmTitle: string;
          ConfirmText: string;
          Success: string;
          Fail: string;
        };
        Contact: string;
        ContactType: string;
        Supervisor: string;
        Anniversary: string;
        BirthDate: string;
        Positions: string;
        Documents: {
          Title: string;
        };
        Departments: {
          Title: string;
          History: string;
        };
        Status: {
          Title: string;
        };
      };
    };
    EmployeeEdit: {
      Title: string;
      NewEmployee: string;
      Breadcrumb: {
        Title: string;
      };
      Save: string;
      BasicInfo: string;
      FirstName: {
        Label: string;
        Validation: string;
      };
      LastName: {
        Label: string;
        Validation: string;
      };
      Email: {
        Label: string;
        Validation: string;
        WrongFormat: string;
      };
      Phone: {
        Label: string;
        Validation: string;
        WrongFormat: string;
      };
      Positions: {
        Label: string;
        Validation: string;
        DateFrom: string;
        DateTo: string;
        Add: string;
        Remove: string;
        PositionValidation: {
          Empty: string;
          EqualsLastPosition: string;
        };
        DateFromValidation: string;
      };
      Departments: {
        Label: string;
        DateFrom: string;
        Remove: string;
        Add: string;
        Validation: string;
        ValidationWasAdded: string;
      };
      Supervisor: {
        Label: string;
      };
      Helpdesk: {
        Label: string;
        Checkbox: string;
      };
      CanShowTicketsForColleaguesFromOwnDepartments: {
        Label: string;
        Checkbox: string;
      };
      ManDayCharge: {
        Label: string;
        Validation: string;
      };
      Status: {
        Title: string;
        Label: string;
      };
      Company: {
        Label: string;
        Validation: string;
      };
      ContractType: {
        Label: string;
        Employee: string;
        IndividualBusinessman: string;
        ExternalSupplier: string;
      };
      AnniversaryDate: {
        Label: string;
      };
      BirthDate: {
        Label: string;
      };
      Documents: {
        Label: string;
      };
      SaveSuccess: string;
      SaveFail: string;
    };
  };
  InvoiceItemCategories: {
    Title: string;
    Breadcrumb: {
      Title: string;
    };
    List: {
      Name: string;
      Code: string;
      ISCode: string;
      Division: string;
    };
    Detail: {
      TitleCreate: string;
      TitleEdit: string;
      BreadcrumbCreate: string;
      BreadcrumbEdit: string;
      Name: string;
      Code: string;
      ISCode: string;
      Division: string;
      Save: string;
      SaveSuccess: string;
      SaveFail: string;
      BtnCreate: string;
      Validation: {
        Required: string;
        MaxLength: string;
      };
    };
  };
  Settings: {
    Title: string;
  };
  Common: {
    Search: string;
    Create: string;
    Save: string;
    Cancel: string;
    Delete: string;
    Settings: string;
    Yes: string;
    No: string;
    Autocomplete: {
      NoOptions: string;
      Loading: string;
      Clear: string;
      Open: string;
      Close: string;
    };
    DatePicker: {
      MinDate: string;
    };
    App: {
      Name: string;
      SubTitle: string;
    };
    TablePagination: {
      BackButton: string;
      NextButton: string;
      RowsPerPage: string;
      DisplayedRows: string;
    };
    ConfirmRedirect: {
      Title: string;
      Close: string;
      Success: string;
    };
    Column: {
      Detail: {
        Tooltip: string;
      };
    };
    Documents: {
      Show: string;
      Add: string;
      Remove: string;
      Name: {
        Label: string;
        Validation: string;
      };
      Url: {
        Label: string;
        Validation: string;
        NoDriveLink: string;
      };
    };
  };
}

export const Resources = createProxy<ResourceDictionary>();

type ProxyProperty = Readonly<{
  path: string;
}>;

export function getResourcePath<TRoot, T>(
  proxy: ObjPathProxy<ResourceDictionary, string>,
): string {
  const path = getPath(proxy);

  if (!path || path.length === 0) {
    return "";
  }
  if (path.length === 1) {
    return path[0].toString();
  }
  return `${path[0].toString()}:${path.slice(1).join(".")}`;
}

export function useResource() {
  const { t: i18Translation } = useTranslation();

  return {
    t: (
      resourcePath: ObjPathProxy<ResourceDictionary, string>,
      options?: object,
    ) => i18Translation(getResourcePath(resourcePath), options),
  };
}

export function useServerError<TRoot, T>(
  parentObject: ObjPathProxy<ResourceDictionary, object>,
  fallbackProperty: ObjPathProxy<ResourceDictionary, string>,
) {
  const { t } = useResource();
  return {
    translateError: (code: string | null | undefined) => {
      if (!code) {
        return null;
      }

      const newCode = `${getResourcePath(parentObject as any)}.${code}`;

      return i18next.exists(newCode) ? i18next.t(newCode) : t(fallbackProperty);
    },
  };
}
